import { type ReactNode, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  DEFAULT_JUMPER_BANDWIDTH_MHZ,
  DEFAULT_JUMPER_DURATION_MINUTES,
  DEFAULT_JUMPER_SUPPRESSION,
  JUMPER_BANDWIDTH_OPTIONS_MHZ,
  MAX_JUMPER_DURATION_MINUTES,
  MAX_JUMPER_SUPPRESSION_MAX_COUNT,
  MAX_JUMPER_SUPPRESSION_TIME_S,
  MIN_JUMPER_DURATION_MINUTES,
  MIN_JUMPER_SUPPRESSION_MAX_COUNT,
  MIN_JUMPER_SUPPRESSION_TIME_S,
  SECONDS_PER_MINUTE,
} from '../../constants/jumper-sessions.js';
import { FREQUENCY_STEP_MHZ } from '../../constants/suppressions.js';
import { useCreateJumperSession, useIntl } from '../../hooks';
import { mhzToHz } from '../../lib/frequency.js';
import { zodResolver } from '../../lib/zod-resolver.js';
import { buildJumperSessionSchema } from '../../validations/jumper-session.js';
import { Button, Input } from '../ui';

type JumperSessionFormInput = {
  startFrequency: string;
  endFrequency: string;
  duration: string;
  suppressionTime: string;
  suppressionMaxCount: string;
  bandwidth: string;
};

type FieldProps = {
  label: string;
  htmlFor: string;
  error?: string;
  className?: string;
  children: ReactNode;
};

const selectClassName =
  'h-10 w-full rounded-md border border-border bg-background-light px-2 text-sm shadow-sm';

function Field({ label, htmlFor, error, className, children }: FieldProps) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      {/* Two jumper labels are full sentences ("Max suppression count per frequency"), which no
          column of this row is wide enough for. `truncate` keeps every field one text line tall so
          the row stays aligned, and `title` keeps the full wording reachable on hover. */}
      <label
        htmlFor={htmlFor}
        title={label}
        className="block truncate text-xs font-medium text-foreground"
      >
        {label}
      </label>
      {children}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

export function JumperPanel({
  deviceId,
  minAllowedMhz,
  maxAllowedMhz,
  disabled,
  onCreated,
}: {
  deviceId: string;
  minAllowedMhz: number | null;
  maxAllowedMhz: number | null;
  disabled: boolean;
  onCreated?: () => void;
}) {
  const intl = useIntl();
  const createJumperSession = useCreateJumperSession();

  const validationSchema = useMemo(
    () => buildJumperSessionSchema(minAllowedMhz, maxAllowedMhz),
    [maxAllowedMhz, minAllowedMhz],
  );

  const defaultValues = useMemo<JumperSessionFormInput>(
    () => ({
      startFrequency: '',
      endFrequency: '',
      duration: String(DEFAULT_JUMPER_DURATION_MINUTES),
      suppressionTime: String(DEFAULT_JUMPER_SUPPRESSION.suppressionTime),
      suppressionMaxCount: String(DEFAULT_JUMPER_SUPPRESSION.suppressionMaxCount),
      bandwidth: String(DEFAULT_JUMPER_BANDWIDTH_MHZ),
    }),
    [],
  );

  const {
    clearErrors,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<JumperSessionFormInput>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues,
  });

  const isFieldDisabled = disabled || isSubmitting || createJumperSession.isPending;
  const isPending = isSubmitting || createJumperSession.isPending;

  const onSubmit = handleSubmit(async (values) => {
    clearErrors('root');

    try {
      await createJumperSession.mutateAsync({
        deviceId,
        payload: {
          startFrequencyHz: mhzToHz(Number(values.startFrequency)),
          endFrequencyHz: mhzToHz(Number(values.endFrequency)),
          durationSeconds: Number(values.duration) * SECONDS_PER_MINUTE,
          suppressionTime: Number(values.suppressionTime),
          suppressionMaxCount: Number(values.suppressionMaxCount),
          bandwidthHz: mhzToHz(Number(values.bandwidth)),
        },
      });
      // The dialog this replaced reset on `open` flipping true, which no longer happens. Radix
      // unmounts an inactive TabsContent, so a tab switch already discards entry — but the tab
      // stays mounted across its own submit, so without this the operator would be looking at the
      // frequencies they just started a session with. Mirrors MultiFrequencyPanel: reset, then
      // notify.
      reset(defaultValues);
      onCreated?.();
    } catch (error) {
      setError('root', {
        type: 'server',
        message:
          error instanceof Error
            ? error.message
            : intl.formatMessage({ id: 'jumperSession.dialog.genericSubmitError' }),
      });
    }
  });

  return (
    // One row, like the sibling suppression tabs. Every field is `flex-1` so the six of them share
    // the section's full width instead of leaving a gap on the right; the `min-w-*` floors are what
    // `flex-wrap` breaks on, so a narrow viewport spills onto a second line rather than squeezing
    // the number inputs down to unreadable.
    <form
      className="flex w-full flex-wrap items-end gap-2"
      data-testid="jumper-session-form"
      onSubmit={onSubmit}
    >
      <Field
        label={intl.formatMessage({ id: 'jumperSession.dialog.startFrequency' })}
        htmlFor="jumper-start-frequency"
        error={errors.startFrequency?.message}
        className="min-w-32 flex-1"
      >
        <Input
          id="jumper-start-frequency"
          type="number"
          step={FREQUENCY_STEP_MHZ}
          suffix="MHz"
          data-testid="jumper-start-frequency"
          disabled={isFieldDisabled}
          {...register('startFrequency')}
          className={errors.startFrequency ? 'border-destructive' : undefined}
        />
      </Field>

      <Field
        label={intl.formatMessage({ id: 'jumperSession.dialog.endFrequency' })}
        htmlFor="jumper-end-frequency"
        error={errors.endFrequency?.message}
        className="min-w-32 flex-1"
      >
        <Input
          id="jumper-end-frequency"
          type="number"
          step={FREQUENCY_STEP_MHZ}
          suffix="MHz"
          data-testid="jumper-end-frequency"
          disabled={isFieldDisabled}
          {...register('endFrequency')}
          className={errors.endFrequency ? 'border-destructive' : undefined}
        />
      </Field>

      <Field
        label={intl.formatMessage({ id: 'jumperSession.dialog.duration' })}
        htmlFor="jumper-duration"
        error={errors.duration?.message}
        className="min-w-28 flex-1"
      >
        <Input
          id="jumper-duration"
          type="number"
          min={MIN_JUMPER_DURATION_MINUTES}
          max={MAX_JUMPER_DURATION_MINUTES}
          suffix="min"
          data-testid="jumper-duration"
          disabled={isFieldDisabled}
          {...register('duration')}
          className={errors.duration ? 'border-destructive' : undefined}
        />
      </Field>

      <Field
        label={intl.formatMessage({ id: 'jumperSession.dialog.suppressionTime' })}
        htmlFor="jumper-suppression-time"
        error={errors.suppressionTime?.message}
        className="min-w-24 flex-1"
      >
        <Input
          id="jumper-suppression-time"
          type="number"
          min={MIN_JUMPER_SUPPRESSION_TIME_S}
          max={MAX_JUMPER_SUPPRESSION_TIME_S}
          suffix="s"
          data-testid="jumper-suppression-time"
          disabled={isFieldDisabled}
          {...register('suppressionTime')}
          className={errors.suppressionTime ? 'border-destructive' : undefined}
        />
      </Field>

      <Field
        label={intl.formatMessage({ id: 'jumperSession.dialog.suppressionMaxCount' })}
        htmlFor="jumper-suppression-max-count"
        error={errors.suppressionMaxCount?.message}
        className="min-w-24 flex-1"
      >
        <Input
          id="jumper-suppression-max-count"
          type="number"
          min={MIN_JUMPER_SUPPRESSION_MAX_COUNT}
          max={MAX_JUMPER_SUPPRESSION_MAX_COUNT}
          data-testid="jumper-suppression-max-count"
          disabled={isFieldDisabled}
          {...register('suppressionMaxCount')}
          className={errors.suppressionMaxCount ? 'border-destructive' : undefined}
        />
      </Field>

      <Field
        label={intl.formatMessage({ id: 'jumperSession.dialog.bandwidth' })}
        htmlFor="jumper-bandwidth"
        error={errors.bandwidth?.message}
        className="min-w-28 flex-1"
      >
        <select
          id="jumper-bandwidth"
          data-testid="jumper-bandwidth"
          disabled={isFieldDisabled}
          {...register('bandwidth')}
          className={errors.bandwidth ? `${selectClassName} border-destructive` : selectClassName}
        >
          {JUMPER_BANDWIDTH_OPTIONS_MHZ.map((mhz) => (
            <option key={`bandwidth-${mhz}`} value={mhz}>
              {`${mhz} MHz`}
            </option>
          ))}
        </select>
      </Field>

      <Button
        type="submit"
        className="shrink-0"
        data-testid="jumper-submit"
        disabled={!isValid || isFieldDisabled}
      >
        {isPending
          ? intl.formatMessage({ id: 'jumperSession.dialog.submitting' })
          : intl.formatMessage({ id: 'jumperSession.dialog.submit' })}
      </Button>

      {/* Basis-full so a server error takes its own line under the row rather than squeezing it. */}
      {errors.root && (
        <span className="basis-full text-sm text-destructive">{errors.root.message}</span>
      )}
    </form>
  );
}
